import {
  PHOTO_BLOCKED_EVENT,
  eventOf,
  routeOfPath,
} from "@/features/open-home/telemetry-events";

type Params = Record<string, string | number | boolean | null | undefined>;

const UUID = /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi;

/** Where the member is when it happens, ids blanked: `/open-home/rooms/:id`. */
const pathNow = () =>
  window.location.pathname
    .replace(/^\/(nl|de|fr|es|it)(?=\/|$)/, "")
    .replace(UUID, ":id") || "/";

/**
 * Open Home events for GTM/GA4, all named `oh_*` so one GTM trigger forwards them all (Thomas,
 * 10-27: "understand what people click on and use and where they drop off"). Parameters are
 * prefixed `oh_` too, so they never collide with the site's own; `gtagEvent` adds the page type,
 * member id and an event id. Ids and field names only: never typed text or amounts a member
 * entered.
 *
 * The provider is loaded on first use, like the share button does: importing it up front drags
 * Apollo into every Open Home test. `oh_path` is taken at the click, so a click that navigates
 * still says where it happened.
 */
export const trackOpenHome = (name: string, params: Params = {}) => {
  if (typeof window === "undefined" || !Array.isArray(window.dataLayer)) {
    return;
  }

  const at = { oh_path: pathNow() };

  void import(
    "@/common/services/analytics/providers/google-analytics.provider"
  ).then(({ gtagEvent }) => gtagEvent(`oh_${name}`, { ...at, ...params }));
};

/**
 * Every change made through the Open Home API, from `openHomeFetch`: `oh_<action>` when it
 * worked (`oh_offer_made`, `oh_photo_uploaded`), `oh_action_failed` with the action and status
 * when it did not, so each step of a flow has its attempts, successes and failures. A photo the
 * safety scan refused is `oh_photo_blocked` with `oh_reason` instead of a failure. Reads are left
 * to the page views.
 */
export const trackOpenHomeAction = (
  method: string,
  path: string,
  status: number,
  extra: Params = {},
  refusal: string | null = null
) => {
  if (method.toUpperCase() === "GET") {
    return;
  }

  const route = routeOfPath(path);
  const event = eventOf(method, route) ?? "other_action";

  if (refusal) {
    trackOpenHome(PHOTO_BLOCKED_EVENT, {
      oh_event: event,
      oh_route: route,
      oh_reason: refusal,
    });
  } else if (status >= 200 && status < 400) {
    trackOpenHome(event, { oh_route: route, ...extra });
  } else {
    trackOpenHome("action_failed", {
      oh_event: event,
      oh_route: route,
      oh_status: status,
      ...extra,
    });
  }
};
